import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";
import "react-toastify/dist/ReactToastify.css";
import "./AddAssignment.css";

const AddAssignment = ({ assignment, closeForm, onAssignmentAdded }) => {
  const [className, setClassName] = useState("");
  const [section, setSection] = useState("");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subject, setSubject] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [date, setDate] = useState("");
  const [day, setDay] = useState("");
  const [attachmentType, setAttachmentType] = useState("text");
  const [textMessage, setTextMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [existingAttachmentUrl, setExistingAttachmentUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState(null);

  const API_BASE = "https://aadyaacademy.org/api";

  useEffect(() => {
    console.log("AddAssignment component rendered");
  }, []);

  // Fetch classes, sections, and subjects from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        setError(null);

        const [classRes, sectionRes, subjectRes] = await Promise.all([
          axios.get(`${API_BASE}/classes`).catch(() => ({ data: { classes: [] } })),
          axios.get(`${API_BASE}/sections`).catch(() => ({ data: { sections: [] } })),
          axios.get(`${API_BASE}/api/subjects`).catch(() => ({ data: [] })),
        ]);

        setClasses(classRes.data.classes || []);
        setSections(sectionRes.data.sections || []);

        const formattedSubjects = Array.isArray(subjectRes.data)
          ? subjectRes.data.map((sub) => ({
              id: sub.id,
              name: sub.names,
            }))
          : [];
        setSubjects(formattedSubjects);

        if (!formattedSubjects.length) toast.warn("No subjects available.", {
          position: "top-right",
          autoClose: 3000,
        });
        if (!sectionRes.data.sections.length) toast.warn("No sections available.", {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (err) {
        setError("Failed to load data. Please try again.");
        toast.error("Failed to load data.", {
          position: "top-right",
          autoClose: 3000,
        });
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, []);

  // Auto-populate date and day from system for new assignments
  useEffect(() => {
    const today = new Date();
    const formattedDate = moment(today).format("YYYY-MM-DD");
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const currentDay = days[today.getDay()];
    setDate(formattedDate);
    setDay(currentDay);
  }, []);

  // Populate form for editing
  useEffect(() => {
    if (assignment) {
      setClassName(assignment.class || "");
      setSection(assignment.section || "");
      setSubject(assignment.subject || "");
      // Normalize date to YYYY-MM-DD
      const formattedDate = assignment.date
        ? moment(assignment.date, ["YYYY-MM-DD", moment.ISO_8601], true).isValid()
          ? moment(assignment.date).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");
      setDate(formattedDate);
      // Set day based on normalized date
      setDay(
        assignment.day ||
          ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][
            moment(formattedDate).toDate().getDay()
          ]
      );
      setExistingAttachmentUrl(assignment.attachmentUrl || "");
      setTextMessage(assignment.text_message || "");
      setAttachmentType(assignment.attachmentUrl ? "file" : "text");
    }
  }, [assignment]);

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (!className || !section || !subject) {
      toast.error("Please fill in the grade, section, and subject fields.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const formData = new FormData();
    formData.append("class", className);
    formData.append("section", section);
    formData.append("subject", subject);
    formData.append("date", date); // Already in YYYY-MM-DD format
    formData.append("day", day);

    if (attachmentType === "text") {
      formData.append("text_message", textMessage);
    } else if (attachment) {
      formData.append("attachment", attachment);
    } else if (existingAttachmentUrl) {
      formData.append("attachmentUrl", existingAttachmentUrl);
    }

    try {
      setLoading(true);
      let response;
      if (assignment) {
        response = await axios.put(`${API_BASE}/assignments/${assignment.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Assignment updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setMessage("Assignment updated successfully!");
        // Delay closing to ensure toast is visible
        setTimeout(() => closeForm(), 1000);
      } else {
        response = await axios.post(`${API_BASE}/assignments`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Assignment added successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setMessage("Assignment added successfully!");
        onAssignmentAdded({
          ...response.data,
          class: className,
          section,
          subject,
          date,
          day,
          text_message: attachmentType === "text" ? textMessage : "",
          attachmentUrl:
            attachmentType === "file" && response.data.attachmentUrl
              ? response.data.attachmentUrl
              : "",
        });
        // Delay closing to ensure toast is visible
        setTimeout(() => closeForm(), 1000);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message || err?.response?.statusText || "Error updating assignment.";
      setMessage(errorMsg);
      toast.error(`Error: ${errorMsg}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setAttachment(file);
  };

  // Construct URL for attachment
  const getAttachmentUrl = (url) => {
    if (!url) return null;
    // Extract the file path (e.g., 'uploads/rtyuiojkjhgfvcsaertyu.pdf') from the URL
    const filePath = url.split('/uploads/')[1];
    return filePath ? `${API_BASE}/uploads/${filePath}` : null;
  };

  // Render loading, error, or form
  if (isLoadingData) return <div>Loading data...</div>;

  if (error) {
    return (
      <div className="add-assignment-container">
        <div className="add-assignment-content">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="assignment-buttons">
            <button className="assignment-cancel" onClick={closeForm}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="add-assignment-container">
      <div className="add-assignment-content">
        <h2>{assignment ? "Edit Assignment" : "Add New Assignment"}</h2>
        {message && <div className="message">{message}</div>}
        <div className="grade-subject">
          <div className="form-group-grade">
            <label>Grade:</label>
            <select value={className} onChange={(e) => setClassName(e.target.value)} required>
              <option value="" disabled>Select Grade</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.name}>{cls.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group-section">
            <label>Section:</label>
            <select value={section} onChange={(e) => setSection(e.target.value)} required>
              <option value="" disabled>Select Section</option>
              {sections.map((sec) => (
                <option key={sec.id} value={sec.name}>{sec.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group-subject">
            <label>Subject:</label>
            <select value={subject} onChange={(e) => setSubject(e.target.value)} required>
              <option value="" disabled>Select Subject</option>
              {subjects.map((sub) => (
                <option key={sub.id} value={sub.name}>{sub.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="date-day">
          <div className="form-group-date">
            <label>Date:</label>
            <div className="date-value">{date}</div>
          </div>
          <div className="form-group-day">
            <label>Day:</label>
            <div className="day-value">{day}</div>
          </div>
        </div>

        <div className="form-group-attachment">
          <label>Attachment Type:</label>
          <select value={attachmentType} onChange={(e) => setAttachmentType(e.target.value)}>
            <option value="text">Text Message</option>
            <option value="file">File Upload</option>
          </select>
        </div>

        {attachmentType === "text" ? (
          <div className="form-group-text-message">
            <label>Text Message:</label>
            <textarea
              placeholder="Enter your message"
              value={textMessage}
              onChange={(e) => setTextMessage(e.target.value)}
            />
          </div>
        ) : (
          <div className="form-group-file">
            <h4>Upload File:</h4>
            <select id="fileType">
              <option value="pdf">PDF</option>
              <option value="doc">DOC</option>
              <option value="docx">DOCX</option>
              <option value="jpg">JPG</option>
              <option value="png">PNG</option>
            </select>
            <input
              type="file"
              id="file"
              accept=".pdf,.doc,.docx,.jpg,.png"
              onChange={handleFileChange}
            />
            {attachment && <p>Uploaded File: {attachment.name}</p>}
            {existingAttachmentUrl && !attachment && (
              <p>
                Existing File:{" "}
                <a href={getAttachmentUrl(existingAttachmentUrl)} target="_blank" rel="noopener noreferrer">
                  View
                </a>
              </p>
            )}
          </div>
        )}

        <div className="assignment-buttons">
          <button
            className="assignment-submit"
            type="button"
            onClick={handleFormSubmit}
            disabled={loading}
          >
            {assignment ? "Update" : "Submit"}
          </button>
          <button
            className="assignment-cancel"
            type="button"
            onClick={closeForm}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAssignment;